import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { authorLabel } from '../../lib/authors.js';
import { BaseComponent } from '../../lib/base-component.js';
import {
    CATALOG_TYPES,
    catalogQuery,
    catalogTypeLabel,
    formatDimensions,
    formatFileSize,
    pageRange,
    pickVersion,
    versionCaption,
} from '../../lib/catalog.js';
import { downloadCatalogFile } from '../../lib/download.js';
import { sizeLabel } from '../../lib/format.js';
import { router } from '../../lib/router.js';
import '../shared/image-lightbox.js';
import '../shared/media-thumb.js';

/**
 * The asset catalog (issue #205): the finished art staff published to it, for
 * freelancers to browse and download so their work matches the house style.
 */
class AssetCatalogView extends BaseComponent {
    static properties = {
        _assets: { type: Array, state: true },
        _total: { type: Number, state: true },
        _hasMore: { type: Boolean, state: true },
        _page: { type: Number, state: true },
        _query: { type: String, state: true },
        _type: { type: String, state: true },
        _tag: { type: String, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true }, // 'list', 'detail'
        _selected: { type: Object, state: true },
        // The version of the selected asset shown and downloaded.
        _version: { type: Number, state: true },
        _lightboxSrc: { type: String, state: true },
        _downloading: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header { margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        .filters { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem; }
        .filters input, .filters select { max-width: 220px; }
        .catalog-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
            gap: 1rem;
            margin-bottom: 1rem;
        }
        .catalog-card {
            border: 1px solid #dee2e6; border-radius: 8px; overflow: hidden;
            background: #fff; cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s;
            display: flex; flex-direction: column;
        }
        .catalog-card:hover { border-color: #adb5bd; box-shadow: 0 0.125rem 0.5rem rgba(0,0,0,0.08); }
        .catalog-card:focus-visible { outline: 2px solid #0d6efd; outline-offset: 2px; }
        .card-body { padding: 0.5rem 0.65rem 0.65rem; }
        .card-slug {
            font-weight: 500; font-size: 0.85rem; color: #0d6efd;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .card-type { font-size: 0.75rem; color: #666; }
        .badge-list { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.35rem; }
        .badge-list .badge { font-weight: 500; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1.5rem; }
        .detail-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; word-break: break-word; }
        .detail-layout { display: flex; flex-wrap: wrap; gap: 2rem; align-items: flex-start; }
        .detail-image { flex: 1 1 360px; min-width: 0; }
        .detail-image img {
            display: block; max-width: 100%; max-height: 70vh; object-fit: contain; margin: 0 auto;
            background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; cursor: zoom-in;
        }
        .detail-props { flex: 0 1 300px; border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; }
        .detail-props h6 { font-weight: 600; margin-bottom: 0.75rem; }
        .prop-row { display: flex; padding: 0.5rem 0; border-bottom: 1px solid #f0f0f0; }
        .prop-row:last-child { border-bottom: none; }
        .prop-label { width: 100px; font-weight: 500; color: #666; font-size: 0.85rem; flex-shrink: 0; }
        .prop-value { flex: 1; font-size: 0.9rem; min-width: 0; word-break: break-word; }
        .versions { margin-top: 1.5rem; }
        .versions h6 { font-weight: 600; margin-bottom: 0.75rem; }
        .version-strip { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .version-thumb {
            width: 110px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden;
            background: #fff; padding: 0; cursor: pointer; text-align: center;
        }
        .version-thumb:hover { border-color: #adb5bd; }
        .version-thumb.selected { border-color: #0d6efd; }
        .version-thumb img { display: block; width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; }
        .version-thumb span { display: block; font-size: 0.75rem; color: #555; padding: 0.2rem 0.25rem; }
    `;

    constructor() {
        super();
        this._assets = [];
        this._total = 0;
        this._hasMore = false;
        this._page = 0;
        this._query = '';
        this._type = '';
        this._tag = '';
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._selected = null;
        this._version = null;
        this._lightboxSrc = '';
        this._downloading = false;
        // Only the newest list request's answer is shown, so a slow answer to
        // an earlier search can't replace a later one.
        this._listRequest = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe(() => this._syncFromRoute());
        this._syncFromRoute();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
        clearTimeout(this._searchDebounce);
    }

    async _syncFromRoute() {
        const path = router.getPath();
        if (path.match(/^\/asset-catalog\/[^/]+$/) && router.params.id) {
            this._view = 'detail';
            await this._loadDetail(router.params.id);
        } else {
            this._view = 'list';
            this._selected = null;
            this._loadAssets();
        }
    }

    async _loadAssets() {
        const request = ++this._listRequest;
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get(
                `/api/catalog?${catalogQuery({ query: this._query, type: this._type, tag: this._tag, page: this._page })}`,
            );
            if (request !== this._listRequest) return;
            this._assets = data.assets || [];
            this._total = data.total || 0;
            this._hasMore = Boolean(data.hasMore);
        } catch (err) {
            if (request !== this._listRequest) return;
            this._error = err.message;
        } finally {
            if (request === this._listRequest) this._loading = false;
        }
    }

    async _loadDetail(id) {
        this._error = null;
        this._selected = null;
        this._version = null;
        try {
            this._selected = await api.get(`/api/catalog/${id}`);
            this._version = this._selected.version;
        } catch (err) {
            this._error = err.message;
        }
    }

    /** Filter changes start back at the first page. */
    _setFilter(changes, { debounce = false } = {}) {
        Object.assign(this, changes);
        this._page = 0;
        clearTimeout(this._searchDebounce);
        if (debounce) {
            this._searchDebounce = setTimeout(() => this._loadAssets(), 250);
        } else {
            this._loadAssets();
        }
    }

    _goToPage(page) {
        this._page = page;
        this._loadAssets();
        this.scrollIntoView({ block: 'start' });
    }

    async _download() {
        this._downloading = true;
        this._error = null;
        try {
            await downloadCatalogFile(this._selected.id, this._version);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._downloading = false;
        }
    }

    _openDetail(a) {
        router.navigate(`/asset-catalog/${a.id}`);
    }

    _renderTags(tags, limit = Infinity) {
        if (!tags || tags.length === 0) return nothing;
        const shown = tags.slice(0, limit);
        return html`
            <div class="badge-list">
                ${shown.map((tag) => html`<span class="badge bg-light text-dark">${tag}</span>`)}
                ${tags.length > shown.length ? html`<span class="badge bg-light text-dark">+${tags.length - shown.length}</span>` : nothing}
            </div>
        `;
    }

    _renderList() {
        const filtered = Boolean(this._query.trim() || this._type || this._tag.trim());
        return html`
            <div class="page-header">
                <h4>Asset Catalog</h4>
            </div>
            <p class="page-subtitle">
                Finished Dice Driven art, to see what exists and match the house style. Open a piece to view it full size or download it.
            </p>

            <div class="filters">
                <input type="search" class="form-control form-control-sm" placeholder="Search by name..." aria-label="Search by name"
                    .value="${this._query}"
                    @input="${(e) => this._setFilter({ _query: e.target.value }, { debounce: true })}">
                <select class="form-select form-select-sm" aria-label="Type"
                    @change="${(e) => this._setFilter({ _type: e.target.value })}">
                    <option value="" ?selected="${this._type === ''}">All types</option>
                    ${CATALOG_TYPES.map((t) => html`<option value="${t.value}" ?selected="${this._type === t.value}">${t.label}</option>`)}
                </select>
                <input type="search" class="form-control form-control-sm" placeholder="Tag" aria-label="Tag"
                    .value="${this._tag}"
                    @input="${(e) => this._setFilter({ _tag: e.target.value }, { debounce: true })}">
            </div>

            ${
                this._loading && this._assets.length === 0
                    ? html`<div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`
                    : html`
                ${
                    this._assets.length === 0
                        ? html`<p class="text-muted text-center py-4">${filtered ? 'No art matches those filters.' : 'No art has been published to the catalog yet.'}</p>`
                        : html`
                    <div class="catalog-grid" style="${this._loading ? 'opacity: 0.6;' : ''}">
                        ${this._assets.map(
                            (a) => html`
                            <div class="catalog-card" role="link" tabindex="0" aria-label="${a.slug}"
                                @click="${() => this._openDetail(a)}"
                                @keydown="${(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this._openDetail(a);
                                    }
                                }}">
                                <media-thumb src="${api.getImageUrl(a.image_url)}" alt="${a.slug}"></media-thumb>
                                <div class="card-body">
                                    <div class="card-slug" title="${a.slug}">${a.slug}</div>
                                    <div class="card-type">${catalogTypeLabel(a.asset_type)}${a.miniature_size ? ` · ${sizeLabel('miniature', a.miniature_size)}` : ''}</div>
                                    ${this._renderTags(a.tags, 3)}
                                </div>
                            </div>
                        `,
                        )}
                    </div>
                `
                }

                ${
                    this._page > 0 || this._hasMore
                        ? html`
                    <div class="d-flex justify-content-between align-items-center">
                        <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._page === 0 || this._loading}" @click="${() => this._goToPage(this._page - 1)}">Previous</button>
                        <span class="text-muted small">${pageRange(this._page, this._assets.length, this._total)}</span>
                        <button class="btn btn-sm btn-outline-secondary" ?disabled="${!this._hasMore || this._loading}" @click="${() => this._goToPage(this._page + 1)}">Next</button>
                    </div>
                `
                        : this._total > 0
                          ? html`<p class="text-muted small text-center">${this._total} ${this._total === 1 ? 'piece' : 'pieces'}</p>`
                          : nothing
                }
            `
            }
        `;
    }

    _renderDetail() {
        const a = this._selected;
        const back = html`
            <a class="back-link" href="/asset-catalog">
                <i class="bi bi-arrow-left"></i> Back to the Asset Catalog
            </a>
        `;
        if (!a) {
            return this._error
                ? back
                : html`${back}<div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`;
        }

        const shown = pickVersion(a, this._version) ?? a;
        const imageUrl = api.getImageUrl(shown.image_url);
        const dimensions = formatDimensions(shown.width, shown.height);
        const versions = a.versions ?? [];
        return html`
            ${back}

            <div class="detail-header">
                <h4>${a.slug}</h4>
                <button class="btn btn-primary" ?disabled="${this._downloading}" @click="${this._download}">
                    <i class="bi bi-download"></i> ${this._downloading ? 'Downloading...' : `Download v${shown.version}`}
                </button>
            </div>

            <div class="detail-layout">
                <div class="detail-image">
                    <img src="${imageUrl}" alt="${a.slug}" title="View full size" @click="${() => (this._lightboxSrc = imageUrl)}">
                </div>
                <div class="detail-props">
                    <h6>Properties</h6>
                    <div class="prop-row">
                        <div class="prop-label">Type</div>
                        <div class="prop-value">${catalogTypeLabel(a.asset_type)}</div>
                    </div>
                    ${
                        a.miniature_size
                            ? html`
                        <div class="prop-row">
                            <div class="prop-label">Size</div>
                            <div class="prop-value">${sizeLabel('miniature', a.miniature_size)}</div>
                        </div>
                    `
                            : nothing
                    }
                    ${
                        a.miniature_type
                            ? html`
                        <div class="prop-row">
                            <div class="prop-label">Kind</div>
                            <div class="prop-value">${a.miniature_type === 'pc' ? 'Player character' : 'Monster'}</div>
                        </div>
                    `
                            : nothing
                    }
                    ${
                        dimensions
                            ? html`
                        <div class="prop-row">
                            <div class="prop-label">Dimensions</div>
                            <div class="prop-value">${dimensions}</div>
                        </div>
                    `
                            : nothing
                    }
                    ${
                        authorLabel(shown.author)
                            ? html`
                        <div class="prop-row">
                            <div class="prop-label">Author</div>
                            <div class="prop-value">${
                                shown.author.url
                                    ? html`<a href="${shown.author.url}" target="_blank" rel="noopener">${authorLabel(shown.author)}</a>`
                                    : authorLabel(shown.author)
                            }</div>
                        </div>
                    `
                            : nothing
                    }
                    <div class="prop-row">
                        <div class="prop-label">File</div>
                        <div class="prop-value">${shown.mime_type}${shown.file_size != null ? ` · ${formatFileSize(shown.file_size)}` : ''}</div>
                    </div>
                    <div class="prop-row">
                        <div class="prop-label">Tags</div>
                        <div class="prop-value">${a.tags?.length ? this._renderTags(a.tags) : html`<span class="text-muted">None</span>`}</div>
                    </div>
                </div>
            </div>

            ${
                versions.length > 1
                    ? html`
                <div class="versions">
                    <h6>Versions (${versions.length})</h6>
                    <div class="version-strip">
                        ${versions.map(
                            (v) => html`
                            <button type="button" class="version-thumb ${v.version === shown.version ? 'selected' : ''}"
                                aria-pressed="${v.version === shown.version}"
                                @click="${() => (this._version = v.version)}">
                                <img src="${api.getImageUrl(v.image_url)}" alt="${a.slug} ${versionCaption(v)}" loading="lazy">
                                <span>${versionCaption(v)}</span>
                            </button>
                        `,
                        )}
                    </div>
                </div>
            `
                    : nothing
            }

            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
        `;
    }
}

customElements.define('asset-catalog-view', AssetCatalogView);
